import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { faEdit, faCheck, faTimes, faHistory } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../components/AuthContext ';
import ChatHistory from './ChatHistory';
import { API_BASE_URL } from '../components/api';

// Convert markdown to HTML for better message formatting
const formatMarkdown = (text) => {
  if (!text) return '';
  
  let html = text;
  
  // Convert **bold** to <strong>
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Convert *italic* to <em>
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  // Convert links [text](url) to <a>
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 underline hover:text-blue-800">$1</a>');
  
  // Split into lines for list processing
  const lines = html.split('\n');
  let result = [];
  let inList = false;
  let listType = null; // 'ul' or 'ol'
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.match(/^[-*]\s/)) {
      // Bullet list item
      if (!inList || listType !== 'ul') {
        if (inList) result.push(`</${listType}>`);
        result.push('<ul class="list-disc ml-6 mt-1 mb-1 space-y-1">');
        inList = true;
        listType = 'ul';
      }
      const content = line.replace(/^[-*]\s/, '');
      result.push(`<li>${content}</li>`);
    } else if (line.match(/^\d+\.\s/)) {
      // Numbered list item
      if (!inList || listType !== 'ol') {
        if (inList) result.push(`</${listType}>`);
        result.push('<ol class="list-decimal ml-6 mt-1 mb-1 space-y-1">');
        inList = true;
        listType = 'ol';
      }
      const content = line.replace(/^\d+\.\s/, '');
      result.push(`<li>${content}</li>`);
    } else {
      // Not a list item
      if (inList) {
        result.push(`</${listType}>`);
        inList = false;
        listType = null;
      }
      
      // Handle headers (after lists to avoid conflicts)
      if (line.match(/^###\s/)) {
        result.push(`<h3 class="font-bold text-md mt-2 mb-1">${line.replace(/^###\s/, '')}</h3>`);
      } else if (line.match(/^##\s/)) {
        result.push(`<h2 class="font-bold text-lg mt-2 mb-1">${line.replace(/^##\s/, '')}</h2>`);
      } else if (line.match(/^#\s/)) {
        result.push(`<h1 class="font-bold text-xl mt-2 mb-1">${line.replace(/^#\s/, '')}</h1>`);
      } else if (line) {
        result.push(`<p class="mb-1">${line}</p>`);
      } else {
        result.push('<br>');
      }
    }
  }
  
  // Close any open list
  if (inList) {
    result.push(`</${listType}>`);
  }
  
  html = result.join('\n');
  
  // Clean up multiple consecutive <br> tags
  html = html.replace(/(<br>\s*){3,}/g, '<br><br>');
  
  return html;
};

const SereniAi = () => {
  // State management
  const [messages, setMessages] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [inputText, setInputText] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [isThreadsLoading, setIsThreadsLoading] = useState(false);
  const [isInitializingThread, setIsInitializingThread] = useState(false);
  const [isSideNavOpen, setIsSideNavOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingCountdown, setRecordingCountdown] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [botName, setBotName] = useState("");
  const [tempBotName, setTempBotName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [isNameLoading, setIsNameLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [followUps, setFollowUps] = useState([]);
  const [contextInfo, setContextInfo] = useState(null);
  const [showContextInfo, setShowContextInfo] = useState(false);
  const [chatStats, setChatStats] = useState({ totalChats: 0, todayChats: 0, avgResponseTime: 0 });
  const [suggestedPrompts, setSuggestedPrompts] = useState([]);
  const [showQuickActions, setShowQuickActions] = useState(true);
  
  // Refs
  const chatContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const { token } = useAuth();

  const getAuthHeaders = useCallback(() => {
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }, [token]);
  // Fetch initial bot name
  useEffect(() => {
    const fetchInitialBotName = async () => {
      setIsNameLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}bot-settings/`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) throw new Error('Failed to fetch bot name');

        const data = await response.json();
        setBotName(data.bot_name);
        setTempBotName(data.bot_name);
      } catch (error) {
        console.error('Error fetching bot name:', error);
      } finally {
        setIsNameLoading(false);
      }
    };

    fetchInitialBotName();
  }, [token]);

  const updateBotName = async (newName) => {
    try {
      const response = await fetch(`${API_BASE_URL}bot-settings/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ bot_name: newName }),
      });

      if (!response.ok) throw new Error('Failed to update bot name');

      const data = await response.json();
      return data.bot_name;
    } catch (error) {
      console.error('Error updating bot name:', error);
      throw error;
    }
  };

  const handleNameUpdate = async () => {
    if (tempBotName.trim() === '') {
      setTempBotName(botName);
      setIsEditing(false);
      return;
    }
  
    setIsSaving(true);
    try {
      setUpdateError(null);
      const updatedName = await updateBotName(tempBotName);
      setBotName(updatedName);
      setTempBotName(updatedName);
      window.location.reload();
      setIsEditing(false);
    } catch (error) {
      setUpdateError('Failed to update bot name. Please try again.');
      setTempBotName(botName);
    } finally {
      setIsSaving(false);
    }
  };

    // Loading spinner component
    const LoadingSpinner = () => (
      <div className="animate-spin w-4 h-4">
        <svg className="w-full h-full text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      </div>
    );

  const fetchThreadList = useCallback(async () => {
    if (!token) {
      setThreads([]);
      return [];
    }

    try {
      setIsThreadsLoading(true);
      const response = await fetch(`${API_BASE_URL}chat/threads/`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch threads (${response.status})`);
      }

      const data = await response.json();
      const threadList = data?.threads || [];
      setThreads(threadList);
      return threadList;
    } catch (error) {
      console.error('Error fetching threads:', error);
      setThreads([]);
      return [];
    } finally {
      setIsThreadsLoading(false);
    }
  }, [token, getAuthHeaders]);

  const loadThreadMessages = useCallback(async (threadId) => {
    if (!threadId || !token) {
      return;
    }

    setIsInitializingThread(true);
    try {
      const response = await fetch(`${API_BASE_URL}chat/threads/${threadId}/messages/?limit=200`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch messages for thread ${threadId}`);
      }

      const data = await response.json();
      const flattened = [];
      (data?.messages || []).forEach((conv) => {
        if (conv.user_message) {
          flattened.push({ text: conv.user_message, isBot: false });
        }
        if (conv.bot_response) {
          flattened.push({ text: conv.bot_response, isBot: true });
        }
      });

      setMessages(flattened);
      setActiveThreadId(threadId);
      setConversationId(threadId);
    } catch (error) {
      console.error('Error loading thread messages:', error);
      setMessages([]);
    } finally {
      setIsInitializingThread(false);
    }
  }, [token, getAuthHeaders]);

  const createThread = useCallback(async (title = 'New Chat') => {
    if (!token) {
      return null;
    }

    try {
      const response = await fetch(`${API_BASE_URL}chat/threads/`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ title }),
      });

      if (!response.ok) {
        throw new Error(`Failed to create thread (${response.status})`);
      }

      const data = await response.json();
      setThreads((prev) => [data, ...(prev || [])]);
      return data.id;
    } catch (error) {
      console.error('Error creating thread:', error);
      return null;
    }
  }, [token, getAuthHeaders]);

  const initializeThreadSession = useCallback(async () => {
    if (!token) {
      setThreads([]);
      setActiveThreadId(null);
      setConversationId(null);
      setMessages([]);
      return;
    }

    try {
      setIsInitializingThread(true);
      const threadList = await fetchThreadList();

      if (threadList.length > 0) {
        await loadThreadMessages(threadList[0].id);
      } else {
        const newThreadId = await createThread();
        if (newThreadId) {
          setActiveThreadId(newThreadId);
          setConversationId(newThreadId);
          setMessages([]);
        }
      }
    } catch (error) {
      console.error('Error initializing chat threads:', error);
    } finally {
      setIsInitializingThread(false);
    }
  }, [token, fetchThreadList, loadThreadMessages, createThread]);

  useEffect(() => {
    initializeThreadSession();
  }, [initializeThreadSession]);

  const handleSelectThread = useCallback(async (threadId) => {
    if (!threadId || threadId === activeThreadId) {
      return;
    }
    await loadThreadMessages(threadId);
  }, [activeThreadId, loadThreadMessages]);

  const handleNewChat = useCallback(async () => {
    const newThreadId = await createThread();
    if (newThreadId) {
      setActiveThreadId(newThreadId);
      setConversationId(newThreadId);
      setMessages([]);
    }
  }, [createThread]);

  // Fetch follow-ups
  const fetchFollowUps = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}chat/follow-ups/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setFollowUps(data.follow_ups || []);
      }
    } catch (error) {
      console.error('Error fetching follow-ups:', error);
    }
  }, [token]);

  // Resolve follow-up
  const resolveFollowUp = async (followUpId) => {
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}chat/follow-ups/${followUpId}/resolve/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Remove from follow-ups list
        setFollowUps(prev => prev.filter(f => f.id !== followUpId));
      }
    } catch (error) {
      console.error('Error resolving follow-up:', error);
    }
  };

  // Fetch chat statistics
  const fetchChatStats = useCallback(async () => {
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}chat/analytics/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setChatStats({
          totalChats: data.total_sessions || 0,
          todayChats: data.sessions_today || 0,
          avgResponseTime: data.avg_response_time || 0,
        });
      }
    } catch (error) {
      console.error('Error fetching chat stats:', error);
    }
  }, [token]);

  // Generate suggested prompts based on context
  const generateSuggestedPrompts = useCallback(() => {
    const prompts = [];
    
    if (contextInfo) {
      if (contextInfo.mood_data_points > 0) {
        prompts.push("How has my mood been trending lately?");
      }
      if (contextInfo.primary_concerns && contextInfo.primary_concerns.length > 0) {
        prompts.push(`Help me with ${contextInfo.primary_concerns[0]}`);
      }
    }
    
    prompts.push("I'm feeling anxious right now");
    prompts.push("Share my wellness goals progress");
    prompts.push("I need someone to talk to");
    
    setSuggestedPrompts(prompts.slice(0, 4));
  }, [contextInfo]);

  // Fetch follow-ups on mount and thread changes
  useEffect(() => {
    if (token) {
      fetchFollowUps();
      fetchChatStats();
    }
  }, [token, activeThreadId, fetchFollowUps, fetchChatStats]);

  // Update suggested prompts when context changes
  useEffect(() => {
    generateSuggestedPrompts();
  }, [generateSuggestedPrompts]);

  const handleSendMessage = async (message) => {
    setMessages(prev => [...prev, { text: message, isBot: false }]);
    setIsLoading(true);
    
    // Debug logging
    console.log('=== SereniAI Message Send Debug ===');
    console.log('API URL:', `${API_BASE_URL}chat/comprehensive/`);
    console.log('Token exists:', !!token);
    console.log('Token preview:', token ? `${token.substring(0, 20)}...` : 'NO TOKEN');
    console.log('Active Thread ID:', activeThreadId);
    
    try {
      // Use comprehensive chat endpoint with full context awareness
      const response = await fetch(`${API_BASE_URL}chat/comprehensive/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          message: message,
          thread_id: activeThreadId
        }),
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error response:', errorText);
        throw new Error(`Backend returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      
      console.log('=== Response Data ===');
      console.log('Full data:', data);
      console.log('data.response:', data.response);
      console.log('data.thread_id:', data.thread_id);
      console.log('Context used:', data.context_used);
      
      // Update thread ID if new thread created
      if (data.thread_id && data.thread_id !== activeThreadId) {
        console.log('Updating thread ID from', activeThreadId, 'to', data.thread_id);
        setActiveThreadId(data.thread_id);
      }
      
      // Store context info for display
      if (data.context_used) {
        setContextInfo(data.context_used);
      }
      
      // Add bot response to messages
      const botMessage = {
        text: data.response, 
        isBot: true,
        conversationId: data.conversation_id,
        contextUsed: data.context_used
      };
      console.log('Adding bot message:', botMessage);
      setMessages(prev => [...prev, botMessage]);
      
      // Refresh follow-ups after each message
      fetchFollowUps();
      console.log('Message handling complete!');
      
    } catch (backendError) {
      console.error('=== CATCH BLOCK TRIGGERED ===');
      console.error('Error type:', backendError.constructor.name);
      console.error('Error message:', backendError.message);
      console.error('Full error:', backendError);
      console.error('Stack:', backendError.stack);
      
      // Provide specific error guidance
      let errorMessage = "I'm having trouble connecting right now. ";
      
      if (backendError.message.includes('401')) {
        errorMessage += "Your session may have expired. Please try logging in again.";
      } else if (backendError.message.includes('500')) {
        errorMessage += "Our servers are experiencing issues. Please try again in a few minutes.";
      } else if (backendError.message.includes('NetworkError') || backendError.message.includes('Failed to fetch')) {
        errorMessage += "Please check your internet connection and try again.";
      } else {
        errorMessage += "Please try again in a moment. If this persists, contact team@serenimind.com.ng";
      }
      
      setMessages(prev => [...prev, { 
        text: errorMessage, 
        isBot: true, 
        isError: true 
      }]);
    } finally {
      setIsLoading(false);
    }
  };
  

  // Image handling
  const handleMoodDetection = async (file) => {
    const formData = new FormData();
    formData.append('image', file);
  
    setMessages((prev) => [
      ...prev,
      {
        text: "Analyzing your mood from the image...",
        isBot: true,
      },
    ]);
  
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}detect-mood/`, {
        method: 'POST',
        body: formData,
      });
  
      const data = await response.json();
      if (response.ok) {
        setMessages((prev) => [
          ...prev,
          {
            text: `Based on your facial expression, you appear to be ${data.mood}. Would you like to talk about what's on your mind?`,
            isBot: true,
          },
        ]);
      } else {
        throw new Error(data.error || 'Mood detection failed');
      }
    } catch (error) {
      console.error('Error detecting mood:', error);
      setMessages((prev) => [
        ...prev,
        {
          text: "Sorry, I couldn't analyze your mood. Please try again later.",
          isBot: true,
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };
  

  // Voice recording handlers
  const startVoiceRecording = () => {
    if (isRecording) {
      stopVoiceRecording();
      return;
    }

    if ('webkitSpeechRecognition' in window) {
      recognitionRef.current = new window.webkitSpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      setRecordingCountdown(3);
      
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }

      countdownIntervalRef.current = setInterval(() => {
        setRecordingCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownIntervalRef.current);
            try {
              recognitionRef.current.start();
            } catch (error) {
              console.error('Speech recognition error:', error);
              setMessages(prev => [...prev, { 
                text: "There was an error starting voice recording. Please try again.", 
                isBot: true,
                isError: true
              }]);
            }
            return null;
          }
          return prev - 1;
        });
      }, 1000);

      recognitionRef.current.onstart = () => {
        setIsRecording(true);
        setMessages(prev => [...prev, { 
          text: "🎤 Voice recording started. Speak clearly...", 
          isBot: true 
        }]);
      };

      recognitionRef.current.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setInputText(transcript);
        setMessages(prev => [...prev, { 
          text: "Voice recording completed! You can edit the text before sending.", 
          isBot: true 
        }]);
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
        recognitionRef.current = null;
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        setMessages(prev => [...prev, { 
          text: "Sorry, there was an error with voice recording. Please try again.", 
          isBot: true,
          isError: true
        }]);
        recognitionRef.current = null;
      };
    } else {
      alert('Speech recognition is not supported in your browser.');
    }
  };

  const stopVoiceRecording = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    setRecordingCountdown(null);
    setIsRecording(false);
  };

  // Event handlers
  const handleVoiceButtonClick = () => {
    if (isRecording) {
      stopVoiceRecording();
    } else {
      startVoiceRecording();
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      const imageURL = URL.createObjectURL(file);
      // Add the image to the chat messages
      setMessages((prev) => [
        ...prev,
        {
          text: null, // No text for image message
          image: imageURL, // Set the image URL
          isBot: false,
        },
      ]);
      handleMoodDetection(file); // Process the image for mood detection
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      // Add the image to the chat messages
      setMessages((prev) => [
        ...prev,
        {
          text: null, // No text for image message
          image: imageURL, // Set the image URL
          isBot: false,
        },
      ]);
      handleMoodDetection(file); // Process the image for mood detection
    }
  };
  

  const handleInputChange = (e) => setInputText(e.target.value);
  
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const sendMessage = () => {
    if (inputText.trim()) {
      handleSendMessage(inputText);
      setInputText("");
    }
  };

  // Render functions
  const renderMessage = (message) => (
    <div>
      {message.image && (
        <img 
          src={message.image} 
          alt="Uploaded for mood analysis" 
          className="max-w-xs rounded-lg mb-2"
        />
      )}
      {message.text.split('\n').map((line, index) => (
        <span key={index}>
          {line}
          {index !== message.text.split('\n').length - 1 && <br />}
        </span>
      ))}
    </div>
  );

  const renderBotNameSection = () => (
    <div className="flex items-center justify-between p-4 border-b">
      <div className="flex items-center space-x-2">
        {isNameLoading ? (
          <div className="flex items-center space-x-2">
            <div className="h-7 w-32 bg-gray-200 animate-pulse rounded"></div>
            <div className="h-7 w-7 bg-gray-200 animate-pulse rounded-full"></div>
          </div>
        ) : (
          <>
            <h1 className="text-xl font-semibold">Chat with {botName}</h1>
            <button
              onClick={() => setIsEditing(true)}
              className="p-1.5 text-gray-500 hover:text-green-500 hover:bg-gray-100 rounded-full transition-all duration-200"
              title="Edit bot name"
            >
              <FontAwesomeIcon icon={faEdit} className="w-4 h-4" />
            </button>
          </>
        )}
      </div>
    </div>
  );

  // Quick action handler
  const handleQuickAction = (prompt) => {
    setInputText(prompt);
    setShowQuickActions(false);
    // Auto-send the message
    setTimeout(() => {
      handleSendMessage(prompt);
    }, 100);
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50">
      {/* Side Navigation (unchanged) */}
      <div className={`  transform ${isSideNavOpen ? 'translate-x-0' : '-translate-x-full'} w-[280px] bg-white shadow-lg transition-transform duration-300 ease-in-out z-20 md:translate-x-0 smallscreensidenav`}>
      <div className="p-6">
       <nav>
         <ul className="space-y-2">
           <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded"><Link to="/sereniai" className="flex items-center font-semibold">🤖 SereniAI</Link></button></li>
           <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded">          <li><Link to="/book-professionals" className="flex items-center font-semibold">📚 Book our Professionals</Link></li>
           </button></li>
           <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded">
           <Link to="/activity-lounge" className="flex items-center font-semibold">🎮 Activity Lounge</Link>
           </button></li>
           <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded">
           <Link to="/blog" className="flex items-center font-semibold">📝 Blog</Link>
           </button></li>
           <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded">
           <Link to="/journal" className="flex items-center font-semibold">✍🏼 Your Journal</Link>
           </button></li>
           <li><button className="w-full text-left py-2 px-4 hover:bg-gray-100 rounded">
           <Link to="/community" className="flex items-center font-semibold">👥 Community</Link>
           </button></li>
         </ul>
       </nav>
      </div>
      
      </div>
      {/* Main Chat Interface */}
      <div className="flex-1 flex flex-col max-w-5xl mx-auto px-1 py-1 md:py-4">
        
        {/* Chat Statistics Dashboard */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-md p-5 border-2 border-blue-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-blue-700">Total Conversations</h3>
              <span className="text-2xl">💬</span>
            </div>
            <p className="text-3xl font-bold text-blue-900">{chatStats.totalChats}</p>
            <p className="text-xs text-blue-600 mt-1">All time chats</p>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-md p-5 border-2 border-green-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-green-700">Today's Sessions</h3>
              <span className="text-2xl">📅</span>
            </div>
            <p className="text-3xl font-bold text-green-900">{chatStats.todayChats}</p>
            <p className="text-xs text-green-600 mt-1">Chats today</p>
          </div>

          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-md p-5 border-2 border-purple-200">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-purple-700">Active Follow-ups</h3>
              <span className="text-2xl">⚡</span>
            </div>
            <p className="text-3xl font-bold text-purple-900">{followUps.length}</p>
            <p className="text-xs text-purple-600 mt-1">Pending reminders</p>
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-2xl flex flex-col h-[80vh] border-2 border-green-100"
             onDragEnter={handleDrag}
             onDragLeave={handleDrag}
             onDragOver={handleDrag}
             onDrop={handleDrop}>
          
          {/* Drag & Drop Overlay */}
          {dragActive && (
            <div className="absolute inset-0 bg-green-50 bg-opacity-90 flex items-center justify-center rounded-2xl z-50">
              <div className="text-center p-6 rounded-lg">
                <div className="text-4xl mb-2">📸</div>
                <p className="text-green-600 font-medium">Drop your image here for mood analysis</p>
              </div>
            </div>
          )}

          {/* Chat Header - Enhanced */}
          <div className="flex justify-between items-center mb-4 pb-4 pl-4 sm:pl-6 pr-4 pt-4 rounded-t-2xl bg-gradient-to-r from-green-400 to-blue-400 shadow-md">
        <div className="flex items-center space-x-2 sm:space-x-3">
          <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white rounded-full flex items-center justify-center shadow-lg">
            <span className="text-xl sm:text-2xl">🤖</span>
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold text-white">{botName}</h1>
            <p className="text-[10px] sm:text-xs text-green-50 hidden sm:block">Your AI Mental Wellness Companion</p>
          </div>
          <button
            onClick={() => setIsEditing(true)}
            className="text-sm text-white hover:bg-white hover:bg-opacity-20 rounded-lg px-2 py-1 transition-all hidden sm:inline-block"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
        </div>
        <button
          onClick={() => setShowHistory(true)}
          className="flex items-center gap-2 px-3 py-2 sm:px-4 bg-white text-green-600 rounded-lg hover:bg-green-50 transition-colors shadow-md"
          title="View chat history"
        >
          <FontAwesomeIcon icon={faHistory} />
          <span className="text-sm font-medium hidden sm:inline">History</span>
        </button>
      </div>

      {/* Chat History Modal */}
      {showHistory && (
        <ChatHistory
          onClose={() => setShowHistory(false)}
          threads={threads}
          onSelectThread={handleSelectThread}
          onCreateThread={handleNewChat}
          onRefreshThreads={fetchThreadList}
          isThreadsLoading={isThreadsLoading}
          activeThreadId={activeThreadId}
        />
      )}

      {/* Modal */}
      {isEditing && (
  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    <div className="bg-white rounded-xl shadow-xl p-6 w-96 transform transition-all duration-200 scale-100">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-800">Customize Bot Name</h2>
        <button
          onClick={() => {
            setIsEditing(false);
            setTempBotName(botName);
            setUpdateError(null);
          }}
          className="p-1.5 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-200"
          disabled={isSaving}
        >
          <FontAwesomeIcon icon={faTimes} className="w-4 h-4" />
        </button>
      </div>
      
      <div className="space-y-4">
        <div>
          <label htmlFor="botName" className="block text-sm font-medium text-gray-700 mb-1">
            Bot Name
          </label>
          <div className="relative">
            <input
              id="botName"
              type="text"
              value={tempBotName}
              onChange={(e) => setTempBotName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none transition-all duration-200"
              placeholder="Enter bot name..."
              disabled={isSaving}
            />
          </div>
        </div>

        {updateError && (
          <div className="bg-red-50 text-red-600 px-4 py-2 rounded-lg text-sm">
            {updateError}
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4">
          <button
            onClick={() => {
              setIsEditing(false);
              setTempBotName(botName);
              setUpdateError(null);
            }}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200"
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            onClick={handleNameUpdate}
            disabled={isSaving}
            className={`px-4 py-2 text-white bg-green-500 hover:bg-green-600 rounded-lg flex items-center justify-center space-x-2 transition-all duration-200 min-w-[100px] ${
              isSaving ? 'opacity-75 cursor-not-allowed' : ''
            }`}
          >
            {isSaving ? (
              <LoadingSpinner />
            ) : (
              <>
                <FontAwesomeIcon icon={faCheck} className="w-4 h-4" />
                <span>Save</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  </div>
)}

    {/* Chat Messages */}
    <div className="flex flex-col overflow-auto space-y-4 p-6 h-full bg-gradient-to-b from-gray-50 to-white" ref={chatContainerRef}>
  
  {/* Empty State with Quick Actions */}
  {messages.length === 0 && !isLoading && (
    <div className="flex flex-col items-center justify-center h-full py-12 px-4">
      <div className="text-center mb-8">
        <div className="text-5xl sm:text-6xl mb-4">🤖💚</div>
        <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-2">Welcome to SereniAI!</h2>
        <p className="text-sm sm:text-base text-gray-600 max-w-md mx-auto">
          I'm here to support your mental wellness journey. How can I help you today?
        </p>
      </div>

      {/* Quick Action Buttons */}
      {showQuickActions && suggestedPrompts.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 w-full max-w-2xl px-4">
          {suggestedPrompts.map((prompt, index) => (
            <button
              key={index}
              onClick={() => handleQuickAction(prompt)}
              className="bg-gradient-to-r from-green-50 to-blue-50 hover:from-green-100 hover:to-blue-100 border-2 border-green-200 rounded-xl p-4 text-left transition-all transform hover:scale-105 shadow-md hover:shadow-lg"
            >
              <div className="flex items-center space-x-3">
                <span className="text-xl sm:text-2xl">
                  {index === 0 ? '😟' : index === 1 ? '🎯' : index === 2 ? '💚' : '🌟'}
                </span>
                <p className="text-sm font-semibold text-gray-700">{prompt}</p>
              </div>
            </button>
          ))}
        </div>
      )}

      <div className="mt-8 text-xs sm:text-sm text-gray-500 flex items-center gap-2">
        <span>💡</span>
        <p>Or type your own message below to get started</p>
      </div>
    </div>
  )}

  {/* Follow-up Reminders */}
  {followUps.length > 0 && messages.length > 0 && (
    <div className="mb-4 space-y-2">
      {followUps.map((followUp) => (
        <div key={followUp.id} className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-lg shadow-sm">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-1">
                <span className={`text-xs font-semibold px-2 py-1 rounded ${
                  followUp.priority === 'high' ? 'bg-red-100 text-red-700' :
                  followUp.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-green-100 text-green-700'
                }`}>
                  {followUp.priority.toUpperCase()}
                </span>
                <h4 className="font-semibold text-sm text-blue-900">{followUp.topic}</h4>
              </div>
              <p className="text-sm text-blue-800">{followUp.description}</p>
              {followUp.follow_up_message && (
                <p className="text-xs text-blue-600 mt-1 italic">"{followUp.follow_up_message}"</p>
              )}
            </div>
            <button
              onClick={() => resolveFollowUp(followUp.id)}
              className="ml-3 text-blue-600 hover:text-blue-800 text-xs font-medium"
            >
              Dismiss
            </button>
          </div>
        </div>
      ))}
    </div>
  )}
  
  {/* Context Info Display */}
  {contextInfo && (
    <div className="mb-3">
      <button
        onClick={() => setShowContextInfo(!showContextInfo)}
        className="text-xs text-gray-500 hover:text-gray-700 flex items-center space-x-1"
      >
        <span>{showContextInfo ? '▼' : '▶'}</span>
        <span>Context: {contextInfo.history_messages} messages, {contextInfo.mood_data_points} mood entries</span>
      </button>
      {showContextInfo && contextInfo && (
        <div className="mt-2 bg-gray-50 rounded-lg p-3 text-xs space-y-1">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="font-semibold text-gray-600">History:</span>
              <span className="ml-1 text-gray-700">{contextInfo.history_messages} messages</span>
            </div>
            <div>
              <span className="font-semibold text-gray-600">Mood Data:</span>
              <span className="ml-1 text-gray-700">{contextInfo.mood_data_points} entries</span>
            </div>
            {contextInfo.primary_concerns && contextInfo.primary_concerns.length > 0 && (
              <div className="col-span-2">
                <span className="font-semibold text-gray-600">Tracking:</span>
                <span className="ml-1 text-gray-700">{contextInfo.primary_concerns.join(', ')}</span>
              </div>
            )}
            {contextInfo.follow_ups_pending > 0 && (
              <div className="col-span-2">
                <span className="font-semibold text-gray-600">Follow-ups:</span>
                <span className="ml-1 text-gray-700">{contextInfo.follow_ups_pending} pending</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )}
  
  {/* Existing Messages - Enhanced */}
  {messages.map((message, index) => (
    <div
      key={index}
      className={`flex items-start gap-2 sm:gap-3 ${message.isBot ? 'justify-start' : 'justify-end'}`}
    >
      {/* Bot Avatar */}
      {message.isBot && (
        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-green-400 to-blue-400 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
          <span className="text-base sm:text-lg">🤖</span>
        </div>
      )}

      <div className="flex flex-col gap-1 max-w-[85%] sm:max-w-md md:max-w-lg lg:max-w-2xl">
        {/* Message Bubble */}
        <div
          className={`rounded-2xl px-3 py-2 sm:px-4 sm:py-3 shadow-md ${
            message.isBot 
              ? 'bg-gradient-to-br from-gray-50 to-gray-100 text-gray-800 border-2 border-gray-200' 
              : 'bg-gradient-to-r from-green-500 to-blue-500 text-white'
          }`}
        >
          {message.text && (
            <div 
              dangerouslySetInnerHTML={{ __html: formatMarkdown(message.text) }}
              className="markdown-content text-base sm:text-sm leading-relaxed"
            />
          )}
          {message.image && (
            <img
              src={message.image}
              alt="Uploaded"
              className="w-32 h-32 object-cover rounded-lg mt-2"
            />
          )}
        </div>

        {/* Timestamp */}
        <div className={`text-xs sm:text-[10px] text-gray-400 px-2 ${message.isBot ? 'text-left' : 'text-right'}`}>
          {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        </div>
      </div>

      {/* User Avatar */}
      {!message.isBot && (
        <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
          <span className="text-base sm:text-lg">👤</span>
        </div>
      )}
    </div>
  ))}
  
  {/* Loading Animation - Enhanced */}
  {isLoading && (
    <div className="flex items-start gap-2 sm:gap-3 justify-start mb-4">
      <div className="w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-green-400 to-blue-400 rounded-full flex items-center justify-center shadow-md flex-shrink-0 animate-pulse">
        <span className="text-base sm:text-lg">🤖</span>
      </div>
      <div className="bg-gradient-to-br from-blue-50 to-green-50 text-gray-800 max-w-[85%] sm:max-w-md md:max-w-lg lg:max-w-2xl px-4 py-3 sm:px-5 sm:py-4 rounded-2xl border-2 border-blue-200 shadow-md">
        <div className="flex items-center space-x-2 mb-1">
          <div className="animate-pulse flex space-x-1">
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
          </div>
          <span className="text-sm font-medium text-gray-700">{botName} is preparing your response...</span>
        </div>
        <div className="text-xs text-gray-500 ml-6">
          {contextInfo && (
            <span>
              Analyzing {contextInfo.history_messages} conversation{contextInfo.history_messages !== 1 ? 's' : ''}
              {contextInfo.mood_data_points > 0 && ` and ${contextInfo.mood_data_points} mood entr${contextInfo.mood_data_points !== 1 ? 'ies' : 'y'}`}
            </span>
          )}
          {!contextInfo && <span>Gathering conversation context...</span>}
        </div>
      </div>
    </div>
  )}
</div>


          {/* Recording Countdown Overlay */}
          {recordingCountdown && (
            <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-2xl">
              <div className="text-6xl text-white font-bold animate-pulse">
                {recordingCountdown}
              </div>
            </div>
          )}

          {/* Input Area - Enhanced */}
          <div className="border-t-2 border-gray-100 p-6 bg-gradient-to-b from-white to-gray-50">
            <div className="flex items-center gap-2 mb-2">
              {/* Mood Detection Button */}
              <div className="relative group">
                <button
                  onClick={() => fileInputRef.current.click()}
                  className="p-2 text-gray-500 hover:text-green-500 hover:bg-gray-50 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
                <div className="absolute bottom-full left-[80px] transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                  Upload or drop an image for mood analysis
                </div>
              </div>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept="image/*"
                className="hidden"
              />

              {/* Voice Input Button with Tooltip */}
              <div className="relative group">
    <button
      onClick={handleVoiceButtonClick}
      className={`p-2 hover:bg-gray-50 rounded-full transition-colors ${
        isRecording ? 'text-red-500 animate-pulse' : 'text-gray-500 hover:text-green-500'
      }`}
      disabled={recordingCountdown !== null}
    >
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
          d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
      </svg>
    </button>
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-1 bg-gray-800 text-white text-sm rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
      {isRecording ? 'Click to stop recording' : 'Click to start voice recording'}
    </div>
  </div>
            </div>

            {/* Suggested Follow-ups */}
            {messages.length > 0 && suggestedPrompts.length > 0 && showQuickActions && (
              <div className="mb-3 flex flex-wrap gap-2">
                <span className="text-xs text-gray-500 font-medium">Suggested:</span>
                {suggestedPrompts.slice(0, 3).map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => handleQuickAction(prompt)}
                    className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1 rounded-full border border-blue-200 transition-all"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            )}

            {/* Message Input Area */}
            <div className="flex items-center bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl px-4 py-2 border-2 border-gray-200 focus-within:border-green-400 transition-all">
              <textarea
                value={inputText}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                placeholder={`Type your message to ${botName}...`}
                className="flex-1 bg-transparent border-none text-sm focus:outline-none resize-none py-2 min-h-[20px] max-h-[120px]"
                rows="1"
                disabled={isRecording}
              />
              <button 
                onClick={sendMessage}
                className={`ml-2 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-full p-3 hover:from-green-600 hover:to-blue-600 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all shadow-md hover:shadow-lg transform hover:scale-105 ${
                  isLoading || isRecording ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isLoading || isRecording}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              </button>
            </div>

            {/* Recording Status */}
            {isRecording && (
              <div className="mt-2 text-center">
                <span className="text-sm text-red-500 flex items-center justify-center">
                  <span className="animate-pulse mr-2">●</span>
                  Recording... Click the microphone again to stop
                </span>
              </div>
            )}

            {/* Feedback Message */}
            <p className="text-xs text-gray-400 mt-2 text-center">
              {botName} can make mistakes. Please provide 
              <Link to="/feedback" className="text-green-500 hover:underline ml-1">feedback</Link>.
            </p>
          </div>
          {updateError && (
            <p className="text-sm text-red-500 mt-1">{updateError}</p>
            )}
        </div>
      </div>
    </div>
  );
};

export default SereniAi;
